/**
 * Fotos vom Handy vor dem Hochladen verkleinern: spart mobiles Datenvolumen und Speicher,
 * und die Vorschaubilder für die Ordneransicht entstehen gleich mit.
 */
const MAX_EDGE = 2560;
const THUMB_EDGE = 480;

export interface PreparedUpload {
  file: File;
  thumb?: Blob;
}

function canvasBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
}

function draw(bitmap: ImageBitmap, maxEdge: number): HTMLCanvasElement {
  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Kein Zeichenbereich");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  return canvas;
}

/** Zeitstempel als Dateiname, wenn die Kamera nur „image.jpg“ liefert. */
export function photoName(date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `Foto ${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())} ${p(date.getHours())}.${p(date.getMinutes())}.${p(date.getSeconds())}.jpg`;
}

export async function prepareUpload(file: File, fromCamera: boolean): Promise<PreparedUpload> {
  const isImage = /^image\/(jpeg|png|webp|heic|heif)$/i.test(file.type) || /\.(jpe?g|png|webp|heic)$/i.test(file.name);
  if (!isImage) return { file };
  try {
    // Die Ausrichtung aus den Kameradaten wird dabei berücksichtigt.
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const full = draw(bitmap, MAX_EDGE);
    const thumbCanvas = draw(bitmap, THUMB_EDGE);
    bitmap.close();
    const [big, thumb] = await Promise.all([canvasBlob(full, 0.85), canvasBlob(thumbCanvas, 0.72)]);
    if (!big) return { file };
    const base = fromCamera || /^image\.\w+$/i.test(file.name) ? photoName(new Date(file.lastModified || Date.now())) : file.name.replace(/\.\w+$/, ".jpg");
    // Nur ersetzen, wenn das Ergebnis wirklich kleiner ist (z. B. bei bereits kleinen PNG-Grafiken nicht).
    const out = big.size < file.size || !/\.jpe?g$/i.test(file.name) ? new File([big], base, { type: "image/jpeg" }) : file;
    return { file: out, thumb: thumb ?? undefined };
  } catch {
    // Format, das der Browser nicht lesen kann (z. B. HEIC in manchen Browsern): unverändert hochladen.
    return { file };
  }
}
